import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { sendRequest } from '../../utils/dateHelpers';

const McpAuthorization = () => {
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('authToken');
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const finish = useCallback(
    async approved => {
      setSubmitting(true);
      setError('');
      try {
        const response = await sendRequest(
          `/oauth/mcp/authorize/${approved ? 'approve' : 'deny'}`,
          'POST',
          {
            client_id: params.get('client_id'),
            redirect_uri: params.get('redirect_uri'),
            scope: params.get('scope'),
            state: params.get('state'),
            code_challenge: params.get('code_challenge'),
            code_challenge_method: params.get('code_challenge_method')
          }
        );
        const redirectUrl =
          response?.redirectUrl || response?.data?.redirectUrl;
        if (!redirectUrl)
          throw new Error('Authorization response was incomplete.');
        sessionStorage.removeItem('mcpAuthorizationRequest');
        window.location.assign(redirectUrl);
      } catch (requestError) {
        setError(
          requestError?.message ||
            'GitMe could not complete this authorization.'
        );
        setSubmitting(false);
      }
    },
    [params]
  );

  if (!token) {
    sessionStorage.setItem('mcpAuthorizationRequest', location.search);
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-boxdark-2">
      <section className="w-full max-w-lg rounded-xl border border-stroke bg-white p-7 shadow-default dark:border-strokedark dark:bg-boxdark">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          GitMe MCP
        </p>
        <h1 className="mt-2 text-2xl font-semibold text-black dark:text-white">
          Allow app access?
        </h1>
        <p className="mt-3 text-sm leading-6 text-body dark:text-bodydark">
          This app will act with your current GitMe role and company access. It
          cannot access data outside your permissions.
        </p>
        <dl className="mt-5 space-y-3 rounded-lg bg-slate-50 p-4 text-sm dark:bg-white/5">
          <div>
            <dt className="text-body dark:text-bodydark">Client</dt>
            <dd className="mt-0.5 break-all font-medium text-black dark:text-white">
              {params.get('client_id') || 'Unknown client'}
            </dd>
          </div>
          <div>
            <dt className="text-body dark:text-bodydark">Requested scopes</dt>
            <dd className="mt-0.5 break-words font-medium text-black dark:text-white">
              {params.get('scope') || 'gitme.read'}
            </dd>
          </div>
        </dl>
        {error && <p className="mt-4 text-sm text-danger">{error}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={submitting}
            onClick={() => finish(false)}
            className="rounded border border-stroke px-4 py-2 text-sm font-medium text-body disabled:opacity-50 dark:border-strokedark dark:text-bodydark"
          >
            Deny
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => finish(true)}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? 'Connecting…' : 'Allow access'}
          </button>
        </div>
      </section>
    </main>
  );
};

export default McpAuthorization;
